import { useState, useEffect } from "react";

// @mui material components
import {
  Grid,
  TextField,
  IconButton,
} from "@mui/material";
import Icon from "@mui/material/Icon";

import { DRAWER_FORM_GRID_SPACING, drawerReadonlyFieldSx } from "../../../../lib/ui/dialogFormLayout";
import { DrawerForm, DrawerFormAddButton, DrawerFormRow, DrawerFormSection } from "../../../../components/DrawerForm";
import { FormHiddenField, IconAutocomplete } from "../../../../components/forms";
import { CONTACT_TYPE_OPTIONS, ENTITY_STATUS_OPTIONS } from "../../../../lib/ui/iconSelectOptions";
import { useTranslation } from "react-i18next";

interface Contact {
  name: string;
  type: 'PHONE' | 'MOBILE' | 'EMAIL' | 'OTHER' | 'SIEM' | 'BACKUP' | 'FIREWALL' | 'EPP' | 'XDR' | 'DLP' | 'ISP' | 'FILE_AUDIT' | 'MONITORING_TOOL';
  value: string;
}

interface Source {
  name: string;
  type: 'PHONE' | 'MOBILE' | 'EMAIL' | 'OTHER' | 'SIEM' | 'BACKUP' | 'FIREWALL' | 'EPP' | 'XDR' | 'DLP' | 'ISP' | 'FILE_AUDIT' | 'MONITORING_TOOL';
  value: string;
}

const EditSourceSystemBody = (item: any) => {
  const { t } = useTranslation();
  const dialogItem = item ?? {};
  const { sharedLogoPreview, ...sourceSystemData } = dialogItem;
  
  const [selectedStatus, setSelectedStatus] = useState<string | null>('ACTIVE');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sources, setSources] = useState<Source[]>([]);

  useEffect(() => {
    if (item?.status) {
      setSelectedStatus(item.status);
    }
    if (item?.contacts && item.contacts.length > 0) {
      setContacts(item.contacts);
    } else {
      setContacts([]);
    }
    if (item?.sources && item.sources.length > 0) {
      setSources(item.sources);
    } else {
      setSources([]);
    }
  }, [item]);

  const addContact = () => {
    setContacts([...contacts, { name: '', type: 'EMAIL', value: '' }]);
  };

  const removeContact = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const updateContact = (index: number, field: keyof Contact, value: string) => {
    const updatedContacts = contacts.map((contact, i) => 
      i === index ? { ...contact, [field]: value } : contact
    );
    setContacts(updatedContacts);
  };

  const addSource = () => {
    setSources([...sources, { name: '', type: 'EMAIL', value: '' }]);
  };

  const removeSource = (index: number) => {
    setSources(sources.filter((_, i) => i !== index));
  };

  const updateSource = (index: number, field: keyof Source, value: string) => {
    const updatedSources = sources.map((source, i) => 
      i === index ? { ...source, [field]: value } : source
    );
    setSources(updatedSources);
  };

  return (
    <DrawerForm>
      <DrawerFormSection title={t("form.identity")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={12}>
            <TextField
              fullWidth
              id="id"
              name="id"
              label={t("common.id")}
              variant="outlined"
              size="small"
              value={sourceSystemData?.id || ""}
              InputProps={{ readOnly: true }}
              sx={drawerReadonlyFieldSx}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="sourceName"
              name="sourceName"
              label={t("common.name")}
              variant="outlined"
              size="small"
              defaultValue={sourceSystemData?.name || ""}
              placeholder={t("form.sourceSystemName")}
            />
          </Grid>
          <Grid size={12}>
            <IconAutocomplete
              options={ENTITY_STATUS_OPTIONS}
              value={selectedStatus}
              onChange={setSelectedStatus}
              label={t("common.status")}
              required
              noOptionsText="No status options found"
            />
            <FormHiddenField name="sourceStatus" value={selectedStatus || ""} />
          </Grid>
        </Grid>
      </DrawerFormSection>

      <FormHiddenField
        name="sourceLogo"
        value={sharedLogoPreview ? sharedLogoPreview.split(",")[1] || "" : ""}
      />

      <DrawerFormSection title={t("form.contacts")} action={<DrawerFormAddButton onClick={addContact} label={t("common.add")} />}>
        {contacts.map((contact, index) => (
          <DrawerFormRow key={index}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.name")}
              value={contact.name}
              onChange={(e) => updateContact(index, "name", e.target.value)}
            />
            <IconAutocomplete
              options={CONTACT_TYPE_OPTIONS}
              value={contact.type}
              onChange={(value) => updateContact(index, "type", value || "EMAIL")}
              noOptionsText="No type options found"
              sx={{ minWidth: 132, maxWidth: 148 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.value")}
              value={contact.value}
              onChange={(e) => updateContact(index, "value", e.target.value)}
            />
            <IconButton size="small" onClick={() => removeContact(index)} color="error">
              <Icon>remove</Icon>
            </IconButton>
          </DrawerFormRow>
        ))}
        <FormHiddenField name="contacts" value={JSON.stringify(contacts)} />
      </DrawerFormSection>

      <DrawerFormSection title={t("form.sources")} action={<DrawerFormAddButton onClick={addSource} label={t("common.add")} />}>
        {sources.map((source, index) => (
          <DrawerFormRow key={index}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.name")}
              value={source.name}
              onChange={(e) => updateSource(index, "name", e.target.value)}
            />
            <IconAutocomplete
              options={CONTACT_TYPE_OPTIONS}
              value={source.type}
              onChange={(value) => updateSource(index, "type", value || "EMAIL")}
              placeholder={t("common.type")}
              noOptionsText="No type options found"
              sx={{ minWidth: 132, maxWidth: 148 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.value")}
              value={source.value}
              onChange={(e) => updateSource(index, "value", e.target.value)}
            />
            <IconButton size="small" onClick={() => removeSource(index)} color="error">
              <Icon>remove</Icon>
            </IconButton>
          </DrawerFormRow>
        ))}
        <FormHiddenField name="sources" value={JSON.stringify(sources)} />
      </DrawerFormSection>
    </DrawerForm>
  );
};

export default EditSourceSystemBody;
